import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, GraduationCap, ChevronUp, ChevronDown } from 'lucide-react';
import { Btn, Modal, FormField, Badge, inputCls } from '../ui';
import { useToast } from '../../contexts/NotificationContext';
import {
  getTrainingTypes, createTrainingType,
  updateTrainingType, deleteTrainingType,
} from '../../api';

const EMPTY = { name: '', defaultRecyclingMonths: '', position: 0, isActive: true };

export default function HRTrainingTypeManagement() {
  const toast = useToast();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getTrainingTypes();
      setTypes(data);
    } catch {
      toast.error('Erreur chargement types de formation');
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, position: types.length + 1 });
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      defaultRecyclingMonths: t.defaultRecyclingMonths != null ? String(t.defaultRecyclingMonths) : '',
      position: t.position,
      isActive: t.isActive,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        defaultRecyclingMonths: form.defaultRecyclingMonths !== '' ? parseInt(form.defaultRecyclingMonths) : null,
        position: parseInt(form.position) || 0,
        isActive: form.isActive,
      };
      if (editing) {
        await updateTrainingType(editing.id, payload);
        toast.success('Type mis à jour');
      } else {
        await createTrainingType(payload);
        toast.success('Type ajouté');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur enregistrement');
    }
    setSaving(false);
  };

  const handleDelete = async (t) => {
    try {
      await deleteTrainingType(t.id);
      toast.success('Type supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur suppression');
    }
    setDeleteConfirm(null);
  };

  const movePosition = async (t, dir) => {
    await updateTrainingType(t.id, { position: t.position + dir });
    load();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GraduationCap size={18} className="text-primary" />
          <h2 className="text-lg font-semibold text-gray-800">Types de formation</h2>
        </div>
        <Btn variant="primary" size="sm" onClick={openCreate}>
          <Plus size={14} /> Ajouter
        </Btn>
      </div>

      <p className="text-sm text-gray-500">
        Ces types apparaissent dans le sélecteur lors de l'ajout d'une formation.
        La <strong>durée de recyclage</strong> pré-remplit automatiquement la date limite de recyclage
        à partir de la date de fin de formation.
      </p>

      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">Chargement…</div>
      ) : types.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400">Aucun type configuré.</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 w-8">Pos.</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Nom</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Recyclage par défaut</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Statut</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {types.map((t, i) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => movePosition(t, -1)} disabled={i === 0} className="disabled:opacity-30 hover:text-primary"><ChevronUp size={12} /></button>
                      <button onClick={() => movePosition(t, 1)} disabled={i === types.length - 1} className="disabled:opacity-30 hover:text-primary"><ChevronDown size={12} /></button>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">{t.name}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {t.defaultRecyclingMonths != null ? `${t.defaultRecyclingMonths} mois` : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {t.isActive
                      ? <Badge color="bg-green-100 text-green-700">Actif</Badge>
                      : <Badge color="bg-gray-100 text-gray-500">Inactif</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Btn variant="ghost" size="sm" onClick={() => openEdit(t)}><Pencil size={13} /></Btn>
                      <Btn variant="ghost" size="sm" onClick={() => setDeleteConfirm(t)} className="text-red-500 hover:text-red-600"><Trash2 size={13} /></Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal création / édition */}
      {showModal && (
        <Modal
          title={editing ? 'Modifier le type' : 'Nouveau type de formation'}
          size="sm"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <Btn variant="secondary" size="sm" onClick={() => setShowModal(false)}>Annuler</Btn>
              <Btn variant="primary" size="sm" loading={saving} onClick={handleSave}>Enregistrer</Btn>
            </>
          }
        >
          <div className="space-y-4">
            <FormField label="Nom" required>
              <input className={inputCls} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex : SST" />
            </FormField>
            <FormField label="Durée de recyclage par défaut (mois)">
              <input
                className={inputCls}
                type="number"
                min="1"
                value={form.defaultRecyclingMonths}
                onChange={e => setForm(f => ({ ...f, defaultRecyclingMonths: e.target.value }))}
                placeholder="Ex : 24 (laisser vide si aucune)"
              />
              <p className="text-xs text-gray-400 mt-1">Laissez vide si ce type n'a pas d'échéance de recyclage.</p>
            </FormField>
            <FormField label="Statut">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                <span className="text-sm text-gray-700">Actif (visible dans le sélecteur)</span>
              </label>
            </FormField>
          </div>
        </Modal>
      )}

      {/* Modal confirmation suppression */}
      {deleteConfirm && (
        <Modal
          title="Supprimer le type"
          size="sm"
          onClose={() => setDeleteConfirm(null)}
          footer={
            <>
              <Btn variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>Annuler</Btn>
              <Btn variant="danger" size="sm" onClick={() => handleDelete(deleteConfirm)}>Supprimer</Btn>
            </>
          }
        >
          <p className="text-sm text-gray-700">
            Supprimer <strong>{deleteConfirm.name}</strong> ? Si des formations l'utilisent déjà, la suppression sera bloquée — désactivez-le plutôt.
          </p>
        </Modal>
      )}
    </div>
  );
}
